import { DotLegalOverflowMenu, DotLegalOverflowMenuItem, DotLegalTable, DotLegalTooltip, ITableHeader } from "@dotlegal/dotlegal-ui-components";
import ListPageToolbar from "@common/components/listPageToolbar/ListPageToolbar";
import { useTranslation } from "../../../../localization/useTranslation";
import { Box, SxProps, TableCell, Theme } from "@mui/material";
import FilterBar from "../../filterBar/FilterBar";
import { InternalAssessmentApiModel, InternalAssessmentApiRow, InternalAssessmentViewRow } from "../../../../internalAssessment/InternalAssessments.types";
import { ReactNode } from "react";
import QuestionnaireTemplateLink from "../../../../legalEntity/legalEntityAuditTab/QuestionnaireTemplateLink";
import ComplianceAreaWrapper from "../../complianceAreaWrapper/ComplianceAreaWrapper";
import QuestionnaireEvaluation from "../questionnaireEvaluation/QuestionnaireEvaluation";
import InternalAssessmentReportButtons from "../../../../internalAssessment/internalAssessmentReportButtons/InternalAssessmentReportButtons";
import QuestionnaireRisk from "../questionnaireRisk/QuestionnaireRisk";
import QuestionnaireStatus from "../questionnaireStatus/QuestionnaireStatus";
import useInternalAssessmentTableHooks, { defaultSortOrder, defaultSortProperty, useInternalAssessmentTablePageView } from "./InternalAssessmentTable.hooks";
import { InternalAssessmentColumnKey } from "./InternalAssessmentTable.columns";
import PublicAssessment from "../../../svgs/Public assessment.svg?react";
import { isNullOrWhitespace } from "../../../stringOperations";
import { PlanLimit } from "../../../../auth/planProvider/PlanProvider.types.ts";
import TrialUsageWarning from "../../TrialUsageWarning.tsx";
import TrialExpiredRowWrapper from "../../TrialExpiredRowWrapper.tsx";
import MentionInputRender from "../../mentionInput/MentionInputRender.tsx";
import TruncatedMentionText from "@common/components/mentionInput/TruncatedMentionText";
import DotLegalNoteDialog from "@common/components/dotlegalNote/dotLegalNoteDialog/DotLegalNoteDialog";
import SavedViewControls from "@common/components/savedViews/SavedViewControls";
import SavedViewPicker from "@common/components/savedViews/SavedViewPicker";
import { InternalAssessmentFieldGroupingOrder } from "@common/components/filterPopover/FilterPopover.types";
import { FilterPage } from "@common/pageView/PageView.types";
import { ClientFilteredPageView } from "@common/pageView/useClientFilteredPageView";

export interface InternalAssessmentTableSavedViews {
    page: FilterPage.InternalAssessments | FilterPage.PublicInternalAssessments;
    // The export's sheet tab and the start of its filename.
    reportName: string;
}

export interface IInternalAssessmentTableProps {
    internalAssessmentModel: InternalAssessmentApiModel | undefined;
    isLoading: boolean;
    refetch?: () => void;
    readOnly?: boolean;
    emptyState: ReactNode;
    getMenuItems: (row: InternalAssessmentApiRow) => Array<DotLegalOverflowMenuItem>;
    planLimit?: PlanLimit;
    sessionStorageKey?: string;
    showPublicTemplateColumn?: boolean;
    // Narrows the columns, the search and the filters to what differs between the assessments in a group - see
    // InternalAssessmentTable.columns.ts.
    withinAssessmentGroup?: boolean;
    // Adds saved views, the column picker and the export. Opt-in so the entity tabs embedding this table keep the
    // toolbar they already have.
    savedViews?: InternalAssessmentTableSavedViews;
}

export interface InternalAssessmentTableContentProps extends IInternalAssessmentTableProps {
    pageView?: ClientFilteredPageView<InternalAssessmentColumnKey>;
}

// A column without a cell is registered for sorting and searching only - DotLegalTable drops any header marked
// hideHeader before rendering.
interface InternalAssessmentColumn {
    header: ITableHeader<InternalAssessmentViewRow>;
    cell?: (row: InternalAssessmentViewRow) => ReactNode;
    cellSx?: SxProps<Theme>;
}

// Saved views are fetched per page, and a query cannot be skipped from inside the table, so only the tables that
// opt in mount the hook that fetches them.
function InternalAssessmentTable(props: IInternalAssessmentTableProps) {
    if (props.savedViews) {
        return <InternalAssessmentTableWithSavedViews {...props} savedViews={props.savedViews} />;
    }

    return <InternalAssessmentTableContent {...props} />;
}

function InternalAssessmentTableWithSavedViews(props: IInternalAssessmentTableProps & { savedViews: InternalAssessmentTableSavedViews }) {
    const pageView = useInternalAssessmentTablePageView(props.savedViews.page, props.showPublicTemplateColumn);

    return <InternalAssessmentTableContent {...props} pageView={pageView} />;
}

function InternalAssessmentTableContent(props: InternalAssessmentTableContentProps) {
    const { translateString } = useTranslation();

    const {
        isLoading,
        searchString,
        setSearchString,
        columnLabels,
        columnOrder,
        searchExclusions,
        tableFields,
        pageView,
        onExportClick,
        audits,
        hasData,
        filterOptions,
        onFilterChange,
        pinnedFilterIds,
        onPinnedFilterIdsChange,
        getReportPdfUrl,
        isDownloadingPdf,
        onDownloadReportAsPdf,
        hasActiveFilters,
        getMenuItems,
        noteDialogProps,
    } = useInternalAssessmentTableHooks(props);

    function getColumns(): Record<InternalAssessmentColumnKey, InternalAssessmentColumn> {
        return {
            name: {
                header: { property: "name", text: columnLabels.name, align: "left", showOnMobile: true },
                cell: getName,
                cellSx: { minWidth: 250 },
            },
            uniqueIdentifier: {
                header: { property: "uniqueIdentifier", text: columnLabels.uniqueIdentifier, align: "left", showOnMobile: true },
                cell: (row) => row.uniqueIdentifier,
            },
            sent: {
                header: { property: "sent", text: columnLabels.sent, align: "left", showOnMobile: true },
                cell: (row) => row.sentString,
            },
            deadline: {
                header: { property: "deadline", text: columnLabels.deadline, align: "left", showOnMobile: true },
                cell: (row) => row.deadlineString,
            },
            responsibleName: {
                header: { property: "responsibleName", text: columnLabels.responsibleName, align: "left", showOnMobile: true },
                cell: (row) => row.responsibleName,
            },
            assigneeName: {
                header: { property: "assigneeName", text: columnLabels.assigneeName, align: "left", showOnMobile: true },
                cell: (row) => row.assigneeName,
            },
            questionnaireName: {
                header: { property: "questionnaireName", text: columnLabels.questionnaireName, align: "left", showOnMobile: true },
                cell: (row) => <QuestionnaireTemplateLink type={"audit"} id={row.assessmentTemplate.id} name={row.assessmentTemplate.name} />,
            },
            complianceAreas: {
                header: { property: "complianceAreasString", text: columnLabels.complianceAreas, align: "left", showOnMobile: true },
                cell: getComplianceAreas,
            },
            sender: {
                header: { property: "sender", text: columnLabels.sender, align: "left", showOnMobile: true },
                cell: (row) => row.sender,
            },
            publicTemplateName: {
                header: { property: "publicTemplateName", text: columnLabels.publicTemplateName, align: "left", showOnMobile: true },
                cell: (row) => row.publicTemplateName,
            },
            status: {
                header: { property: "statusString", text: columnLabels.status, align: "left", showOnMobile: true },
                cell: getStatus,
            },
            riskLevel: {
                header: { property: "riskLevel", text: columnLabels.riskLevel, align: "left", showOnMobile: true },
                cell: getRisk,
            },
            score: {
                header: { property: "scoreName", text: columnLabels.score, align: "left", showOnMobile: true },
                cell: (row) => row.scoreName,
                cellSx: { textAlign: "right" },
            },
            assessmentReport: {
                header: { property: "questionnaireId", text: columnLabels.assessmentReport, align: "left", showOnMobile: true },
                cell: getReportButtons,
            },
            evaluation: {
                header: { property: "evaluation", text: columnLabels.evaluation, align: "left", showOnMobile: true },
                cell: (row) => (
                    <QuestionnaireEvaluation evaluation={row.partOfCurrentPlan ? row.evaluation : undefined} evaluationStatus={row.evaluationStatus} />
                ),
            },
            evaluationName: {
                header: { property: "evaluationName", text: columnLabels.evaluationName, align: "left", showOnMobile: true, hideHeader: true },
            },
            reminderSent: {
                header: { property: "reminderSent", text: columnLabels.reminderSent, align: "left", showOnMobile: true },
                cell: (row) => (row.remindersPaused ? translateString("remindersPaused") : row.reminderSentString),
            },
            note: {
                header: { property: "notePlainText", text: columnLabels.note, align: "left", showOnMobile: true },
                cell: (row) => (row.partOfCurrentPlan ? <TruncatedMentionText value={row.note} /> : undefined),
            },
        };
    }

    function getHeaders() {
        const columns = getColumns();

        return columnOrder.map((key) => columns[key].header);
    }

    function getName(row: InternalAssessmentApiRow) {
        const publicAssessmentTooltip =
            `${translateString("publicInternalAssessment")} ${row.publicTemplateIsDeleted ? `(${translateString("deleted")})` : ""}`.trimEnd();
        const publicAssessment = (
            <Box>
                {row.createdFromPublicTemplate && (
                    <DotLegalTooltip text={`${publicAssessmentTooltip}${isNullOrWhitespace(row.remark) ? "" : ": " + row.remark}`}>
                        <Box sx={{ marginLeft: 0.5 }}>
                            <PublicAssessment />
                        </Box>
                    </DotLegalTooltip>
                )}
            </Box>
        );
        if (row.description) {
            return (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                    <DotLegalTooltip
                        text={isNullOrWhitespace(row.description) ? undefined : <MentionInputRender value={row.description} linkColor={"white"} />}
                    >
                        <Box>{row.name}</Box>
                    </DotLegalTooltip>
                    {publicAssessment}
                </Box>
            );
        }

        return (
            <Box sx={{ display: "flex" }}>
                {row.name} {publicAssessment}
            </Box>
        );
    }

    function getComplianceAreas(row: InternalAssessmentApiRow) {
        return (
            <Box sx={{ display: "flex", gap: 1 }}>
                {row.complianceAreas.map((c) => (
                    <ComplianceAreaWrapper key={c.id} complianceArea={c.name} />
                ))}
            </Box>
        );
    }

    function getStatus(row: InternalAssessmentApiRow) {
        return (
            <QuestionnaireStatus
                showQuestionsStatistics={row.showQuestionsStatistics}
                numberOfQuestionsAnswered={row.numberOfQuestionsAnswered}
                totalNumberOfQuestions={row.totalNumberOfQuestions}
                status={row.status}
                lastestRecipientUpdate={row.lastestRecipientUpdate}
            />
        );
    }

    function getRisk(row: InternalAssessmentApiRow) {
        return <QuestionnaireRisk riskLevel={row.riskLevel} riskName={row.riskName} scoreEnabled={row.scoreEnabled} status={row.status} />;
    }

    function getReportButtons(row: InternalAssessmentApiRow) {
        return (
            <InternalAssessmentReportButtons
                row={row}
                isDownloadingPdf={isDownloadingPdf(row.id)}
                reportPdfUrl={getReportPdfUrl(row, false, row.hasRecommendations)}
                onDownloadReportPdf={onDownloadReportAsPdf}
            />
        );
    }

    return (
        <>
            {hasData ? (
                <>
                    {props.planLimit?.limitExceeded && <TrialUsageWarning textKey={"internalAuditsLimitExceededWarning"} marginBottom={2} />}
                    <ListPageToolbar
                        savedViewsMenu={pageView && <SavedViewPicker pageView={pageView} isLoading={isLoading} />}
                        leftContent={getFilterBar()}
                        rightContent={
                            pageView && (
                                <SavedViewControls
                                    pageView={pageView}
                                    tableFields={tableFields}
                                    groupingOrder={InternalAssessmentFieldGroupingOrder}
                                    isLoading={isLoading}
                                    onExportClick={onExportClick}
                                />
                            )
                        }
                    />
                    <DotLegalTable
                        sessionStateKey={pageView?.tableSessionStateKey}
                        headers={getHeaders()}
                        rowsPerPage={30}
                        searchString={searchString}
                        columnsExcludedFromFilter={searchExclusions}
                        defaultOrderBy={(pageView?.sortOrderBy ?? defaultSortProperty) as keyof InternalAssessmentViewRow}
                        defaultOrder={pageView?.sortOrder ?? defaultSortOrder}
                        onChange={pageView?.onTableStateChange}
                        isLoading={isLoading}
                        clickableRows={false}
                        hasOverflowMenu
                        paginationLabelOf={translateString("labelOf")}
                        emptyText={translateString(hasActiveFilters ? "noDataMatchingCriteria" : "noData")}
                        data={audits ?? []}
                        renderRow={renderRow}
                    />
                </>
            ) : (
                <Box
                    sx={{
                        img: {
                            width: "25%",
                        },
                    }}
                >
                    {props.emptyState}
                </Box>
            )}

            {noteDialogProps && <DotLegalNoteDialog {...noteDialogProps} />}
        </>
    );

    function getFilterBar() {
        return (
            <FilterBar
                isLoading={isLoading}
                filterData={filterOptions}
                fetchFilterData={!isLoading && !!props.internalAssessmentModel}
                onMenuClose={onFilterChange}
                onRemove={onFilterChange}
                pinnedFilterIds={pinnedFilterIds}
                onPinnedFilterIdsChange={onPinnedFilterIdsChange}
                onClearFilter={pageView?.hasFilterChanged ? () => pageView.clearFilterData() : undefined}
                clearFilterText={pageView?.hasFilterChanged ? (pageView.isSavedView ? "savedView" : "defaultView") : undefined}
                searchValue={searchString}
                searchLabel={translateString("search")}
                onSearchChange={(value) => setSearchString(value)}
            />
        );
    }

    function renderRow(row: InternalAssessmentViewRow) {
        const columns = getColumns();

        return (
            <TrialExpiredRowWrapper
                key={row.id}
                tooltip={translateString("internalAssessmentLocked")}
                onNonExpiredClick={() => {}}
                expired={!row.partOfCurrentPlan}
                planUpgradeText={translateString("internalAssessmentLimitReached")}
            >
                {columnOrder
                    .filter((key) => columns[key].cell)
                    .map((key) => (
                        <TableCell key={key} sx={columns[key].cellSx}>
                            {columns[key].cell!(row)}
                        </TableCell>
                    ))}

                <TableCell align={"right"}>{<DotLegalOverflowMenu items={getMenuItems(row)} />}</TableCell>
            </TrialExpiredRowWrapper>
        );
    }
}

export default InternalAssessmentTable;
